import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalProps,
} from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import useCreateOpenApiKey from '../../../../api/mutations/account/useCreateOpenApiKey';
import useGetMyOpenApiKeys from '../../../../api/queries/me/useGetMyOpenApiKeys';
import useCopyToClipboard from '../../../../hooks/useCopyToClipboard';

const CreateApiKeyContent: FC<{ onClose: () => void }> = (props) => {
  const { onClose } = props;
  const [clientCreds, setClientCreds] = useState<ClientCreds | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { openApiKeys } = useGetMyOpenApiKeys();

  const existingApiKeyNames = openApiKeys?.map((key) => key.name) || [];

  const CREATE_API_KEY_SCHEMA = z.object({
    name: z
      .string()
      .min(1, 'Name is required')
      .refine(
        (name) => !existingApiKeyNames.includes(name),
        'An API key with this name already exists. Please choose another name.'
      ),
  });

  const { mutateAsync: createOpenApiKeyMutation } = useCreateOpenApiKey();

  const onSubmit = async (data: z.infer<typeof CREATE_API_KEY_SCHEMA>) => {
    setErrorMessage(null);
    const response = await createOpenApiKeyMutation({
      name: data.name,
    });

    if (
      response.createOpenApiKey?.__typename ===
      'CreateOpenApiKeyResponseSuccess'
    ) {
      setClientCreds({
        clientId: response.createOpenApiKey.clientId,
        clientSecret: response.createOpenApiKey.clientSecret,
      });
    } else {
      setErrorMessage(
        response.createOpenApiKey?.message ||
          'Sorry, there was an error creating your API key'
      );
    }
  };
  const formMethods = useForm<z.infer<typeof CREATE_API_KEY_SCHEMA>>({
    resolver: zodResolver(CREATE_API_KEY_SCHEMA),
  });
  const { handleCopy } = useCopyToClipboard();

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit, console.error)}>
        <Modal.Body>
          {clientCreds ? (
            <div>
              <p className="mt-2 mb-4">
                Anyone who has these credentials will be able to view and update
                data in your Payaca account - only share it with trusted
                partners.
              </p>
              <h4>Client ID</h4>
              <button
                type="button"
                className="p-4 hover:bg-gray-50 cursor-pointer bg-white text-left"
                onClick={() => handleCopy(clientCreds.clientId)}
              >
                <p>
                  {clientCreds.clientId}
                  <UntitledIcon name="copy-07.3" className="ml-4 h-4 w-4" />
                </p>
              </button>

              <h4>Client secret</h4>
              <button
                type="button"
                className="p-4 hover:bg-gray-50 cursor-pointer bg-white text-left"
                onClick={() => handleCopy(clientCreds.clientSecret)}
              >
                <p>
                  {clientCreds.clientSecret}
                  <UntitledIcon name="copy-07.3" className="ml-4 h-4 w-4" />
                </p>
              </button>
              <Alert
                iconName="alert-triangle.3"
                colour="yellow"
                className="mt-4"
              >
                {
                  "Make sure you copy the secret now. You won't be able to see it again!"
                }
              </Alert>
            </div>
          ) : (
            <>
              <Controller
                name="name"
                control={formMethods.control}
                render={({ field: { name, onChange, value } }) => {
                  return (
                    <Field name={name}>
                      <Field.Label>Name</Field.Label>
                      <Field.HelperText>
                        Individual or service using the API key.
                      </Field.HelperText>
                      <Input value={value} onChange={onChange} />
                    </Field>
                  );
                }}
              />
              {errorMessage && (
                <Alert
                  colour="red"
                  className="max-h-24 overflow-scroll sm:max-h-none sm:overflow-auto mt-2"
                >
                  <p>{errorMessage}</p>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            {clientCreds ? (
              <Button onClick={onClose}>Close</Button>
            ) : (
              <Button type="submit">Create API key</Button>
            )}
          </Modal.Footer.Actions>
        </Modal.Footer>
      </form>
    </FormProvider>
  );
};

type ClientCreds = {
  clientId: string;
  clientSecret: string;
};
type Props = Omit<ModalProps, 'title'> & { onClose: () => void };
const CreateApiKeyModal = (props: Props) => {
  return (
    <Modal {...props} title="Create API key">
      <CreateApiKeyContent onClose={props.onClose} />
    </Modal>
  );
};

export default CreateApiKeyModal;
